import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Card,
    Group,
    ScrollArea,
    Skeleton,
    Table,
    Text,
    Title,
    Tooltip
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconUserPlus } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";
import StudentAccountCreatorModal from "./StudentAccountCreatorModal";
import StudentPasswordEditorModal from "./StudentPasswordEditorModal";
import StudentUsernameEditorModal from "./StudentUsernameEditorModal";

interface ClassroomUsersTableProps {
    classroomId: string;
}

const ClassroomUsersTable: React.FC<ClassroomUsersTableProps> = ({ classroomId }) => {
    const [users, setUsers] = useState<User[] | null | undefined>(undefined);
    const [userToSetPasswordFor, setUserToSetPasswordFor] = useState<User | null>(null);
    const [userToSetUsernameFor, setUserToSetUsernameFor] = useState<User | null>(null);
    const [showCreateStudentAccountModal, setShowCreateStudentAccountModal] = useState(false);

    const session = useContext(SessionContext);
    if (session === undefined) {
        throw new Error("This component must be a child of SessionContext.");
    }

    const handleTutorialCompletionOverride = useCallback(async (user: User, completionStatus: boolean) => {
        if (session.token === null) {
            return;
        }

        // Display a notification that deletion is in progress.
        const notificationId = Utils.displayNotification(
            "Updating tutorial completion status...",
            undefined,
            "blue",
            "loading"
        );

        try {
            // Call the API to delete the classroom.
            await ConductionAPI.setTutorialCompletionStatus(user.id, completionStatus, session.token);

            Utils.updateNotification(
                notificationId,
                "Tutorial completion status updated!",
                undefined,
                "green",
                <IconCheck size={16} />
            );
        } catch (error) {
            console.error("Error creating tokens", error);
            Utils.updateNotification(
                notificationId,
                "We are sorry. An unexpected error occurred when updating the tutorial completion status.",
                undefined,
                "red",
                <IconAlertCircle size={16} />,
                true,
                false
            );
        } finally {
            session.fetchAdministratingClassroom(classroomId);
        }
    }, [session.token, session.fetchAdministratingClassroom, classroomId]);

    useEffect(() => {
        const students = session.administratingClassroom === null ? null : session.administratingClassroom?.getClassroomStudents();
        const classroomAdmins = session.administratingClassroom === null ? null : session.administratingClassroom?.getClassroomAdmins();
        if (students == null || classroomAdmins == null) {
            setUsers(students === null || classroomAdmins === null ? null : undefined);
        } else {
            students.forEach(student => student.roles.push("Student"));
            classroomAdmins.forEach(admin => admin.roles.push("ClassroomAdmin"));
            setUsers([...students, ...classroomAdmins]);
        }
    }, [session.administratingClassroom]);

    return (
        <Card
            h="40vh"
            w="90%"
        >
            <StudentAccountCreatorModal opened={showCreateStudentAccountModal} onClose={() => setShowCreateStudentAccountModal(false)} onCreate={() => session.fetchAdministratingClassroom(classroomId)} classroomId={classroomId} />
            <StudentPasswordEditorModal user={userToSetPasswordFor} showModal={userToSetPasswordFor !== null} setShowModal={(show) => { if (!show) setUserToSetPasswordFor(null); }} />
            <StudentUsernameEditorModal user={userToSetUsernameFor} showModal={userToSetUsernameFor !== null} setShowModal={(show) => { if (!show) setUserToSetUsernameFor(null); }} onChange={() => session.fetchAdministratingClassroom(classroomId)} />

            <Group justify="space-between" mb="md">
                <Title>Classroom Members</Title>
                <Button onClick={() => setShowCreateStudentAccountModal(true)} leftSection={<IconUserPlus size={16} />}>Create Student Accounts</Button>
            </Group>

            {users === undefined ? (
                <Skeleton height={"100%"} radius="md" />
            ) : users === null ? (
                <Text>No users found.</Text>
            ) : (
                <ScrollArea>
                    <Table
                        striped
                        stickyHeader
                        highlightOnHover
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Username</Table.Th>
                                <Table.Th>Role</Table.Th>
                                <Table.Th>Completed Intro Tutorial?</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {users.map((user) => {
                                const student = user.roles.includes("Student");
                                const classroomAdmin = user.roles.includes("ClassroomAdmin");
                                const conductionAdmin = user.roles.includes("ConductionAdmin");
                                return (
                                    <Table.Tr key={user.username}>
                                        <Table.Td>{user.username}</Table.Td>
                                        <Table.Td>
                                            {(student) && (
                                                <Badge color="blue">
                                                    Student
                                                </Badge>
                                            )}
                                            {(classroomAdmin) && (
                                                <Badge color="yellow">
                                                    Instructor
                                                </Badge>
                                            )}
                                            {(conductionAdmin) && (
                                                <Badge color="darkGreen">
                                                    Conduction Admin
                                                </Badge>
                                            )}
                                            {(!student && !classroomAdmin && !conductionAdmin) && (
                                                <Badge color="red">
                                                    Other
                                                </Badge>
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Tooltip
                                                withArrow
                                                label={user.completedBeginnerTutorial ? `Reset intro tutorial for ${user.username}` : `Mark the intro tutorial as completed for ${user.username}`}
                                            >
                                                <Button
                                                    size="xs"
                                                    onClick={() => handleTutorialCompletionOverride(user, !user.completedBeginnerTutorial)}
                                                    color={user.completedBeginnerTutorial ? "green" : "red"}
                                                >
                                                    {user.completedBeginnerTutorial ? "Yes" : "No"}
                                                </Button>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            <Group>
                                                <Button
                                                    size="xs"
                                                    disabled={!student || classroomAdmin || conductionAdmin}
                                                    onClick={() => setUserToSetUsernameFor(user)}
                                                >
                                                    Change Username
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    disabled={!student || classroomAdmin || conductionAdmin}
                                                    onClick={() => setUserToSetPasswordFor(user)}
                                                >
                                                    Set New Password
                                                </Button>
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            )}
        </Card>
    );
};

export default ClassroomUsersTable;
